/* ================================================================
   DROP DEAD — Course curriculum progressive enhancement
   Mirrors dd-faq.js:
     - Smooth anchor scroll to a module group with header offset.
     - Active-chip tracking via IntersectionObserver on module groups.
     - Native <details>/<summary> handles the lesson accordion; this
       script is additive only.
   ================================================================ */

(function () {
  'use strict';

  function headerOffset() {
    var val = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    var px = parseInt(val, 10);
    if (isFinite(px) && px > 0) return px + 16;
    return 16;
  }

  function initCurriculum(root) {
    if (!root || root.dataset.ddCourseCurriculumInit === '1') return;
    root.dataset.ddCourseCurriculumInit = '1';

    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-dd-course-chip]'));
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-dd-course-group]'));

    if (!chips.length || !groups.length) return;

    function setActive(moduleId) {
      chips.forEach(function (c) {
        c.classList.toggle('is-active', c.getAttribute('data-dd-course-chip') === moduleId);
      });
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function (event) {
        var href = chip.getAttribute('href') || '';
        var id = href.indexOf('#') === 0 ? href.slice(1) : '';
        var target = id ? document.getElementById(id) : null;
        if (!target) return;

        event.preventDefault();
        var top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset();
        window.scrollTo({ top: top, behavior: 'smooth' });
        setActive(chip.getAttribute('data-dd-course-chip'));

        if (history && history.replaceState) {
          history.replaceState(null, '', '#' + id);
        }
      });
    });

    if (!('IntersectionObserver' in window)) {
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        var best = null;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry;
          }
        });
        if (best) {
          var moduleId = best.target.getAttribute('data-dd-course-group');
          if (moduleId) setActive(moduleId);
        }
      },
      {
        rootMargin: '-30% 0px -55% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );

    groups.forEach(function (g) {
      observer.observe(g);
    });
  }

  /* ────────────────────────────────────────────────────────────
     Instructors-into-curriculum bento relocator

     Visually folds the standalone `dd-course-instructors` section into
     the top of the curriculum's groups column, rendered as compact
     bento cards inside a subtle filled container. Why JS, not Liquid:
     instructor data lives in a sibling section's blocks (so they
     remain customizer-editable independently); Liquid sections can't
     read each other's blocks. Cloning at render time would duplicate
     the deck in two places. Moving the source section's <article>
     children into a freshly-built bento gives us the requested
     UX without a data migration and without DOM duplication.

     Theme-editor parity: when a merchant edits the instructors section,
     Shopify dispatches `shopify:section:unload` (old DOM gone) followed
     by `shopify:section:load` (fresh DOM). We tear down our bento on
     unload and rebuild on the next idle tick — simpler than diffing.

     Customizer behavior: the relocator runs in the theme editor too so
     merchants see the same bento layout that ships to customers. To
     keep the source section editable, we listen for
     `shopify:section:select` / `shopify:block:select` and temporarily
     un-hide the source section while it (or one of its blocks) is the
     active selection — surfacing the click target so the merchant can
     see and edit what they picked.
  ──────────────────────────────────────────────────────────── */

  var INSTRUCTORS_BENTO_SELECTOR = '[data-dd-curriculum-instructors-bento]';
  var INSTRUCTORS_SECTION_SELECTOR = '.dd-course-instructors';

  function buildCompactInstructorCard(sourceCard) {
    var name = sourceCard.querySelector('.dd-course-instructors__name');
    var role = sourceCard.querySelector('.dd-course-instructors__role');
    var portraitImg = sourceCard.querySelector('.dd-course-instructors__portrait img');
    var creds = sourceCard.querySelectorAll('.dd-course-instructors__credentials li');

    var article = document.createElement('article');
    article.className = 'dd-curriculum-instructor-card';

    var avatar = document.createElement('div');
    avatar.className = 'dd-curriculum-instructor-card__avatar';
    if (portraitImg) {
      // Clone the <img> rather than moving it so the original portrait
      // node stays attached to the source card (the source section is
      // hidden, not removed — keeping its DOM intact lets the customizer
      // still find/select it). Strip width/height/sizes so the image
      // can fit the avatar's CSS-driven box without aspect-ratio fights.
      var img = portraitImg.cloneNode(true);
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.removeAttribute('sizes');
      img.removeAttribute('srcset');
      avatar.appendChild(img);
    } else {
      // Initials fallback so the avatar slot doesn't collapse to 0
      var initialsText = name ? name.textContent.trim() : '';
      var parts = initialsText.split(/\s+/);
      var initials = '';
      if (parts[0]) initials += parts[0].charAt(0);
      if (parts[1]) initials += parts[1].charAt(0);
      var span = document.createElement('span');
      span.className = 'dd-curriculum-instructor-card__initials';
      span.textContent = initials.toUpperCase();
      avatar.appendChild(span);
    }
    article.appendChild(avatar);

    var meta = document.createElement('div');
    meta.className = 'dd-curriculum-instructor-card__meta';
    if (name && name.textContent.trim()) {
      var n = document.createElement('h4');
      n.className = 'dd-curriculum-instructor-card__name';
      n.textContent = name.textContent.trim();
      meta.appendChild(n);
    }
    if (role && role.textContent.trim()) {
      var r = document.createElement('p');
      r.className = 'dd-curriculum-instructor-card__role';
      r.textContent = role.textContent.trim();
      meta.appendChild(r);
    }
    if (creds && creds.length) {
      var ul = document.createElement('ul');
      ul.className = 'dd-curriculum-instructor-card__creds';
      // Cap at 3 chips so the compact card stays compact even when the
      // source bio carries a long credential list.
      var max = Math.min(creds.length, 3);
      for (var i = 0; i < max; i++) {
        var li = document.createElement('li');
        li.textContent = creds[i].textContent.trim();
        ul.appendChild(li);
      }
      meta.appendChild(ul);
    }
    article.appendChild(meta);

    return article;
  }

  function teardownInstructorsBento() {
    var existing = document.querySelector(INSTRUCTORS_BENTO_SELECTOR);
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    var hidden = document.querySelectorAll('.dd-course-instructors.is-relocated');
    for (var i = 0; i < hidden.length; i++) {
      hidden[i].classList.remove('is-relocated');
    }
  }

  function isThemeEditor() {
    // Shopify exposes Shopify.designMode === true in the customizer iframe.
    // The relocator runs in both modes; this flag only gates the
    // edit-mode-only "reveal source on selection" affordance below.
    return typeof window !== 'undefined' && !!(window.Shopify && window.Shopify.designMode);
  }

  function relocateInstructorsIntoCurriculum() {
    var groups = document.querySelector('.dd-course-curriculum__groups');
    if (!groups) return;

    teardownInstructorsBento();

    var instructorsSection = document.querySelector(INSTRUCTORS_SECTION_SELECTOR);
    if (!instructorsSection) return;

    var sourceCards = instructorsSection.querySelectorAll('.dd-course-instructors__card');
    if (!sourceCards.length) return;

    var bento = document.createElement('aside');
    bento.className = 'dd-curriculum-instructors-bento';
    bento.setAttribute('data-dd-curriculum-instructors-bento', '');
    bento.setAttribute('aria-label', 'Course instructors');

    var sourceEyebrow = instructorsSection.querySelector('.dd-course-instructors__eyebrow');
    var sourceHeading = instructorsSection.querySelector('.dd-course-instructors__heading');
    if (sourceEyebrow || sourceHeading) {
      var head = document.createElement('header');
      head.className = 'dd-curriculum-instructors-bento__head';
      if (sourceEyebrow && sourceEyebrow.textContent.trim()) {
        var e = document.createElement('span');
        e.className = 'dd-curriculum-instructors-bento__eyebrow';
        e.textContent = sourceEyebrow.textContent.trim();
        head.appendChild(e);
      }
      if (sourceHeading && sourceHeading.textContent.trim()) {
        var h = document.createElement('h3');
        h.className = 'dd-curriculum-instructors-bento__heading';
        h.textContent = sourceHeading.textContent.trim();
        head.appendChild(h);
      }
      bento.appendChild(head);
    }

    var grid = document.createElement('div');
    grid.className = 'dd-curriculum-instructors-bento__grid';
    grid.setAttribute('data-dd-instructor-count', String(sourceCards.length));
    for (var i = 0; i < sourceCards.length; i++) {
      grid.appendChild(buildCompactInstructorCard(sourceCards[i]));
    }
    bento.appendChild(grid);

    groups.insertBefore(bento, groups.firstChild);
    instructorsSection.classList.add('is-relocated');
  }

  function scheduleInstructorsRelocation() {
    // Use rAF so we batch with other layout work after the section
    // mutation, and to give Shopify the editor a moment to settle the
    // section's new HTML before we walk it.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(relocateInstructorsIntoCurriculum);
    } else {
      relocateInstructorsIntoCurriculum();
    }
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-dd-course-curriculum]');
    for (var i = 0; i < roots.length; i++) initCurriculum(roots[i]);
    relocateInstructorsIntoCurriculum();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (!e || !e.target) return;
    var roots = e.target.querySelectorAll
      ? e.target.querySelectorAll('[data-dd-course-curriculum]')
      : [];
    for (var i = 0; i < roots.length; i++) initCurriculum(roots[i]);
    if (e.target.hasAttribute && e.target.hasAttribute('data-dd-course-curriculum')) {
      initCurriculum(e.target);
    }

    // Re-run the bento relocator on either curriculum or instructors
    // reload — easier and more correct than narrowly inspecting which
    // section just changed, since either side reload invalidates the
    // current bento (instructors reload replaces source cards;
    // curriculum reload replaces the destination groups container).
    scheduleInstructorsRelocation();
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (!e || !e.target || !e.target.classList) return;
    // Source section is being torn down → tear down the bento we
    // built from it. The next `shopify:section:load` will rebuild.
    if (
      e.target.classList.contains('dd-course-instructors') ||
      e.target.classList.contains('dd-course-curriculum') ||
      (e.target.querySelector && e.target.querySelector(INSTRUCTORS_SECTION_SELECTOR + ', .dd-course-curriculum'))
    ) {
      teardownInstructorsBento();
    }
  });

  /* ────────────────────────────────────────────────────────────
     Customizer reveal-on-select

     Default state in the editor: source section is hidden (because the
     relocator ran and added `.is-relocated`), so the merchant sees the
     bento — same as the live page. Problem: clicking "Course
     Instructors" or one of its blocks in Shopify's editor sidebar would
     scroll-into-view a `display:none` element. We use the
     section/block select events to flip a counted reveal class on the
     source section while it's the active selection, then drop it on
     deselect. The bento stays in place; the source surfaces alongside
     it for the duration of the edit.

     Refcount via dataset — section-select and block-select can fire
     concurrently (selecting a block also selects its section in some
     editor flows), and we want the reveal to stay until the LAST
     deselect lands.
  ──────────────────────────────────────────────────────────── */

  function findInstructorsSection(target) {
    if (!target) return null;
    if (target.classList && target.classList.contains('dd-course-instructors')) return target;
    if (target.closest) return target.closest(INSTRUCTORS_SECTION_SELECTOR);
    return null;
  }

  function bumpEditReveal(target, delta) {
    if (!isThemeEditor()) return;
    var section = findInstructorsSection(target);
    if (!section) return;
    var current = parseInt(section.dataset.ddEditRevealCount || '0', 10);
    if (!isFinite(current) || current < 0) current = 0;
    current = Math.max(0, current + delta);
    section.dataset.ddEditRevealCount = String(current);
    if (current > 0) {
      section.classList.add('is-edit-revealed');
    } else {
      section.classList.remove('is-edit-revealed');
    }
  }

  document.addEventListener('shopify:section:select', function (e) {
    if (e && e.target) bumpEditReveal(e.target, +1);
  });
  document.addEventListener('shopify:section:deselect', function (e) {
    if (e && e.target) bumpEditReveal(e.target, -1);
  });
  document.addEventListener('shopify:block:select', function (e) {
    if (e && e.target) bumpEditReveal(e.target, +1);
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    if (e && e.target) bumpEditReveal(e.target, -1);
  });
})();
